import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import { useCart, canEditCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { WarehouseSelect } from "../components/WarehouseSelect";
import { LAST_WAREHOUSE_KEY } from "../lib/useWarehouses";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { ResizableTh } from "../components/ResizableTh";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, FileText, FileSpreadsheet, Mail, PackageSearch, Users, Copy,
  List as ListIcon, LayoutGrid, FileOutput, Receipt, ChevronDown, ChevronUp,
  PanelLeftClose, PanelLeftOpen, ArrowUpDown, Columns as ColumnsIcon,
  Maximize2, Minimize2, GripVertical, Lock,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import EmailQuoteDialog from "../components/EmailQuoteDialog";
import QuoteItemOverrideDialog from "../components/QuoteItemOverrideDialog";
import { exportQuotationPdf } from "../lib/exportQuotationPdf";
import { exportQuotationExcel } from "../lib/exportQuotationExcel";
import { SampleShipDialog } from "../components/SampleShipDialog";
import ExportFieldsDialog, { loadExportFields } from "../components/ExportFieldsDialog";
import { formatCurrency } from "../lib/currency";
import ImageLightbox from "../components/ImageLightbox";

function groupByProduct(items) {
  const order = [];
  const map = new Map();
  items.forEach((item) => {
    if (!map.has(item.product_id)) {
      order.push(item.product_id);
      map.set(item.product_id, {
        product_id: item.product_id,
        photo_url: item.photo_url,
        description: item.description,
        specification: item.specification,
        is_overridden: item.is_overridden,
        // iter 121: forward the product-level fields that the Edit Quote
        // Item dialog needs. Without these the dialog opens with empty
        // Company / Customer code inputs even though the list row
        // clearly shows an `item_no`. Variants of the same product share
        // item_no (公司产品编号); customer_product_no can differ per
        // variant so we take the first variant's value as the default.
        item_no: item.item_no,
        customer_product_no: item.customer_product_no,
        customs_duty_rate: item.customs_duty_rate,
        // iter 187 (2026-02): the Edit dialog reads these fields to seed
        // its inputs on open — without forwarding them into the grouped
        // `prod` object, reopening the dialog after a save would show a
        // blank compression_rate / tax_rebate_rate / chinese_name input
        // even though the override was successfully persisted.
        tax_rebate_rate: item.tax_rebate_rate,
        compression_rate: item.compression_rate,
        chinese_name: item.chinese_name,
        // iter 231 (2026-02): 转发 packaging / quality requirement 到 prod
        // 对象,否则打开 Edit Quote Item Dialog 时新加的两个字段永远是空
        // (item 里有值但 groupByProduct 没有透传)。
        packaging_requirement: item.packaging_requirement,
        quality_requirement: item.quality_requirement,
        entry_overrides: item.entry_overrides,
        variants: [],
      });
    }
    map.get(item.product_id).variants.push(item);
  });
  return order.map((id) => map.get(id));
}

function groupItems(items) {
  const groups = [];
  const map = new Map();
  items.forEach((item) => {
    const key = `${item.category || "Uncategorized"} / ${item.subcategory || "General"}`;
    if (!map.has(key)) {
      const group = { key, items: [] };
      map.set(key, group);
      groups.push(group);
    }
    map.get(key).items.push(item);
  });
  groups.forEach((g) => { g.products = groupByProduct(g.items); });
  return groups;
}

const CONTAINER_CBM_MM3 = 65_000_000;
function computeContainerQty(length, width, height, pcs) {
  const l = parseFloat(length), w = parseFloat(width), h = parseFloat(height), p = parseInt(pcs, 10);
  if (!l || !w || !h || !p) return null;
  const qty = Math.floor((CONTAINER_CBM_MM3 * p) / (l * w * h));
  return qty > 0 ? qty : null;
}

// Column order: keep original 9 first, then the newly-added optional columns.
// Product-level columns (photo/description/material/category/bulky) use rowSpan
// across all variants of a product; the rest render per variant row.
const CART_PRODUCT_LEVEL_COLS = new Set(["photo", "description", "material", "category", "bulky", "customs_duty", "tax_rebate"]);
const CART_COLUMN_IDS = [
  "photo", "description", "material", "category", "bulky",
  "size", "pcs_ctn", "ci_qty",
  "gt_sku", "barcode", "carton_dims", "qty_40ft", "gross_weight", "purchase_price",
  "customs_duty", "tax_rebate",
  "price", "final_price", "actions",
];
const CART_COLUMN_LABELS = {
  photo: "Photo", description: "Description", material: "Material",
  category: "Category", bulky: "Bulky",
  size: "Size", pcs_ctn: "Pcs/Ctn", ci_qty: "CI Inv Qty",
  gt_sku: "GT SKU", barcode: "Barcode", carton_dims: "Carton L\u00d7W\u00d7H (cm)",
  qty_40ft: "40' Qty (pcs)", gross_weight: "Gross Wt (kg)", purchase_price: "Factory Cost (RMB)",
  customs_duty: "CA Duty (%)", tax_rebate: "Tax Rebate (%)",
  price: "Price", final_price: "Final Price", actions: "",
};
const CART_VISIBLE_COLS_KEY = "cartVisibleColumns";
const CART_DEFAULT_VISIBLE_COLS = ["photo", "description", "material", "size", "pcs_ctn", "ci_qty", "gt_sku", "price", "final_price", "actions"];
const CART_DEFAULT_COLUMN_WIDTHS = {
  photo: 124, description: 260, material: 180,
  category: 120, bulky: 80,
  size: 90, pcs_ctn: 80, ci_qty: 90,
  gt_sku: 120, barcode: 130, carton_dims: 160, qty_40ft: 112, gross_weight: 90, purchase_price: 130,
  customs_duty: 110, tax_rebate: 110,
  price: 100, final_price: 110, actions: 72,
};
const CART_MIN_COLUMN_WIDTHS = {
  photo: 120, description: 160, material: 100,
  category: 90, bulky: 60,
  size: 60, pcs_ctn: 60, ci_qty: 60,
  gt_sku: 80, barcode: 90, carton_dims: 120, qty_40ft: 80, gross_weight: 70, purchase_price: 90,
  customs_duty: 80, tax_rebate: 80,
  price: 80, final_price: 90, actions: 60,
};
const CART_COLUMN_WIDTHS_STORAGE_KEY = "cartItemsTableColumnWidths_v2";

function QuotationList({ carts, activeCartId, onSelect, onCreate, onRename, onDelete, onDuplicate, onShare, collapsed, onToggleCollapse }) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { cartScope, setCartScope } = useCart();
  const [customerFilter, setCustomerFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  // iter 282: sales-rep (owner) filter options come from the server with per-user counts.
  const [owners, setOwners] = useState({ owners: [], unassigned_count: 0, mine_count: 0, total_count: 0 });
  useEffect(() => {
    api.get("/carts/owners").then(({ data }) => setOwners(data)).catch(() => {});
  }, [carts]);

  const customerOptions = useMemo(() => {
    const names = new Set(carts.map((c) => c.customer_name).filter(Boolean));
    return Array.from(names).sort();
  }, [carts]);

  const visibleCarts = useMemo(() => {
    let list = carts.filter((c) => !c.outside_scope);
    if (customerFilter !== "all") {
      list = list.filter((c) => (customerFilter === "unassigned" ? !c.customer_name : c.customer_name === customerFilter));
    }
    list = [...list].sort((a, b) => {
      const diff = new Date(a.created_at || 0) - new Date(b.created_at || 0);
      return sortOrder === "newest" ? -diff : diff;
    });
    return list;
  }, [carts, customerFilter, sortOrder]);

  if (collapsed) {
    return (
      <div className="w-12 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex flex-col items-center py-4 gap-3" data-testid="quotation-list-panel-collapsed">
        <button
          data-testid="quotation-list-expand-button"
          onClick={onToggleCollapse}
          title="Show quotations"
          className="text-gray-400 hover:text-brand-600 transition-colors"
        >
          <PanelLeftOpen size={18} strokeWidth={1.75} />
        </button>
        <Receipt size={16} className="text-gray-300" strokeWidth={1.75} />
      </div>
    );
  }

  return (
    <div className="w-[440px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="quotation-list-panel">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-gray-400" strokeWidth={1.75} />
          <h1 className="font-heading text-base font-bold text-gray-900">{t("cart.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onCreate} data-testid="quotation-new-button" size="sm" className="rounded-lg bg-brand-600 hover:bg-brand-700 text-xs">
            <Plus size={13} className="mr-1" /> {t("cart.new")}
          </Button>
          <button
            data-testid="quotation-list-collapse-button"
            onClick={onToggleCollapse}
            title={t("cart.hide_quotations")}
            className="text-gray-400 hover:text-brand-600 transition-colors"
          >
            <PanelLeftClose size={17} strokeWidth={1.75} />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 py-1.5 border-b border-gray-100 bg-gray-50/60">
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger data-testid="quotation-customer-filter" className="h-8 text-xs rounded-lg bg-white flex-1">
            <SelectValue placeholder={t("cart.all_customers")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("cart.all_customers")}</SelectItem>
            <SelectItem value="unassigned">{t("cart.unassigned")}</SelectItem>
            {customerOptions.map((name) => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* iter 282: sales-rep filter for everyone. Default "mine"; remembered in localStorage. */}
        <Select value={cartScope} onValueChange={setCartScope}>
          <SelectTrigger data-testid="quotation-owner-filter" className="h-8 text-xs rounded-lg bg-white flex-1">
            <span className="text-gray-400 mr-1">{t("cart.sales_rep")}</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="mine" data-testid="quotation-owner-option-mine">
              <span className="font-semibold text-brand-700">{t("cart.scope_mine")}</span> <span className="text-gray-400">({owners.mine_count})</span>
            </SelectItem>
            <SelectItem value="all" data-testid="quotation-owner-option-all">{t("cart.all_owners")} <span className="text-gray-400">({owners.total_count})</span></SelectItem>
            {owners.unassigned_count > 0 && (
              <SelectItem value="unassigned" data-testid="quotation-owner-option-unassigned">{t("cart.legacy_no_owner")} <span className="text-gray-400">({owners.unassigned_count})</span></SelectItem>
            )}
            {owners.owners.filter((o) => o.user_id !== currentUser?.id).map((o) => (
              <SelectItem key={o.user_id} value={o.user_id} data-testid={`quotation-owner-option-${o.user_id}`}>{o.name} <span className="text-gray-400">({o.count})</span></SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          data-testid="quotation-sort-toggle"
          onClick={() => setSortOrder((s) => (s === "newest" ? "oldest" : "newest"))}
          title={sortOrder === "newest" ? t("cart.newest") : t("cart.oldest")}
          className="h-8 px-2.5 rounded-lg bg-white border border-gray-200 text-xs text-gray-600 hover:text-brand-600 flex items-center gap-1 transition-colors flex-shrink-0"
        >
          <ArrowUpDown size={12} strokeWidth={1.75} />
          {sortOrder === "newest" ? t("cart.newest") : t("cart.oldest")}
        </button>
      </div>
      <Table className="table-fixed">
        <colgroup>
          <col style={{ width: 148 }} />
          <col />
          <col style={{ width: 52 }} />
          <col style={{ width: 108 }} />
        </colgroup>
        <TableHeader>
          <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-100">
            <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">{t("cart.quotation_no")}</TableHead>
            <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold">{t("cart.customer")}</TableHead>
            <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">{t("cart.items")}</TableHead>
            <TableHead className="text-[10px] uppercase tracking-label text-gray-500 font-semibold text-right">{t("dashboard.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleCarts.map((c) => (
            <TableRow
              key={c.id}
              data-testid={`quotation-list-row-${c.id}`}
              onClick={() => onSelect(c.id)}
              className={`cursor-pointer border-b border-gray-50 ${c.id === activeCartId ? "!bg-amber-50 hover:!bg-amber-100" : "hover:bg-gray-50"}`}
            >
              <TableCell className="py-3">
                <span className="text-sm font-semibold text-gray-900 block truncate" title={c.name}>{c.name}</span>
                {/* iter 282: show owner name for quotations that aren't mine. */}
                {c.owner?.name && c.owner.user_id !== currentUser?.id && (
                  <p
                    className="text-[10px] text-brand-700 mt-0.5 truncate flex items-center gap-1"
                    title={c.owner.email || c.owner.name}
                    data-testid={`quotation-list-owner-${c.id}`}
                  >
                    <Users size={9} strokeWidth={2} className="flex-shrink-0" />
                    {c.owner.name}
                    {!canEditCart(c) && <Lock size={9} strokeWidth={2} className="flex-shrink-0 text-gray-400" />}
                  </p>
                )}
                <p className="text-[11px] text-gray-400 mt-0.5">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-US") : "-"}</p>
              </TableCell>
              <TableCell className="text-sm text-gray-500 truncate" title={c.customer_name || "-"}>{c.customer_name || "-"}</TableCell>
              <TableCell className="font-mono text-sm text-right text-gray-500">{c.item_count}</TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-2">
                  {canEditCart(c) && (
                    <button data-testid={`quotation-rename-${c.id}`} onClick={() => onRename(c)} className="text-gray-400 hover:text-brand-600 transition-colors" title="Rename">
                      <Pencil size={13} strokeWidth={1.75} />
                    </button>
                  )}
                  {/* iter 129b: quick-access Duplicate + Share icons per row */}
                  <button data-testid={`quotation-duplicate-${c.id}`} onClick={() => onDuplicate(c)} className="text-gray-400 hover:text-brand-600 transition-colors" title={canEditCart(c) ? "Duplicate" : t("cart.copy_as_mine")}>
                    <Copy size={13} strokeWidth={1.75} />
                  </button>
                  {(c.my_permission === "owner" || c.my_permission == null) && (
                    <button data-testid={`quotation-share-${c.id}`} onClick={() => onShare(c)} className="text-gray-400 hover:text-brand-600 transition-colors" title="Share">
                      <Users size={13} strokeWidth={1.75} />
                    </button>
                  )}
                  {(c.my_permission === "owner" || c.my_permission == null) && (
                    <button data-testid={`quotation-delete-${c.id}`} onClick={() => onDelete(c)} className="text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                      <Trash2 size={13} strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {visibleCarts.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-sm text-gray-400 py-10" data-testid="quotation-list-empty">
                {carts.length === 0 ? t("cart.no_quotations") : t("cart.no_quotations_match")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CartPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { carts, activeCart, activeCartId, setActiveCartId, refreshCarts, createCart, deleteCart, cartScope, setCartScope } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  // iter 282: others' quotations are read-only (my_permission "view"/"read"); explicit "edit" shares + owner/admin can edit.
  const canEdit = canEditCart(activeCart);
  const canShareExport = !!activeCart && activeCart.my_permission !== "view";
  const duplicateCart = async (c) => {
    try {
      const { data } = await api.post(`/carts/${c.id}/duplicate`);
      toast.success(canEditCart(c) ? `Duplicated as "${data.name}"` : t("cart.copied_as_mine", { name: data.name }));
      if (cartScope !== "mine" && cartScope !== "all") setCartScope("mine");
      else await refreshCarts();
      setActiveCartId(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Duplicate failed");
    }
  };

  // iter 148: auto-select a cart when navigated from CustomersPage
  // "history quotations" dialog. Clears the state after applying so the
  // selection isn't re-applied on the next re-render.
  useEffect(() => {
    const target = location.state?.autoSelectCartId;
    if (target && carts?.some((c) => c.id === target)) {
      setActiveCartId(target);
      navigate("/cart", { replace: true });
    }
  }, [location.state, carts, setActiveCartId, navigate]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter 203: 单击行高亮 (session-scoped, 不进 localStorage)。存 product_id
  // 即整个 rowspan 分组一起亮;点同一 product 内任意 variant 都选中整块。
  const [selectedQuoteProductId, setSelectedQuoteProductId] = useState(null);
  // iter 243 (2026-02): 多选 —— 支持勾选多个报价产品并批量删除。以
  // product_id 为单位(一个产品下所有 variants 一起选/删),避免用户
  // 需要逐个 variant 勾选。切换 cart 或成功批量删除后自动清空。
  const [selectedProductIds, setSelectedProductIds] = useState(() => new Set());
  const toggleProductSelected = (productId) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };
  const clearProductSelection = () => setSelectedProductIds(new Set());
  // iter 207: 报价车 items 表格全屏模式,行为跟 Dashboard 一致 (ESC 退出)。
  const [cartFullscreen, setCartFullscreen] = useState(() => localStorage.getItem("cartItemsFullscreen") === "1");
  // iter 220 (2026-02): 图片全屏预览 lightbox。点击表格产品缩略图 → 打开。
  const [lightbox, setLightbox] = useState({ open: false, src: "", alt: "" });
  const toggleCartFullscreen = () => setCartFullscreen((v) => {
    const next = !v;
    localStorage.setItem("cartItemsFullscreen", next ? "1" : "0");
    return next;
  });
  useEffect(() => {
    if (!cartFullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") toggleCartFullscreen(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartFullscreen]);
  const [customers, setCustomers] = useState([]);
  const [itemView, setItemView] = useState("list");
  // iter 237 (2026-02): Grid view 大/中/小三档尺寸,中和小不显示价格。
  const [gridSize, setGridSize] = useState("L");
  const [visibleCartCols, setVisibleCartCols] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(CART_VISIBLE_COLS_KEY) || "null");
      if (Array.isArray(stored) && stored.length) {
        return new Set(stored.filter((id) => CART_COLUMN_IDS.includes(id)));
      }
    } catch {}
    return new Set(CART_DEFAULT_VISIBLE_COLS);
  });
  const [cartColsOpen, setCartColsOpen] = useState(false);
  // iter 131: user-editable column order. Loaded from localStorage;
  // legacy users without a saved order fall back to CART_COLUMN_IDS.
  const CART_COLUMN_ORDER_KEY = "cart-column-order-v1";
  const [cartColOrder, setCartColOrder] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(CART_COLUMN_ORDER_KEY) || "null");
      if (Array.isArray(stored) && stored.length) {
        // Merge with CART_COLUMN_IDS so any newly added columns still appear
        // even if the user's saved order doesn't know about them.
        const known = stored.filter((id) => CART_COLUMN_IDS.includes(id));
        const missing = CART_COLUMN_IDS.filter((id) => !known.includes(id));
        return [...known, ...missing];
      }
    } catch {}
    return [...CART_COLUMN_IDS];
  });
  const moveCartCol = (id, dir) => setCartColOrder((prev) => {
    const idx = prev.indexOf(id);
    if (idx < 0) return prev;
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= prev.length) return prev;
    const next = [...prev];
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    return next;
  });
  const saveCartColLayout = () => {
    localStorage.setItem(CART_COLUMN_ORDER_KEY, JSON.stringify(cartColOrder));
    localStorage.setItem(CART_VISIBLE_COLS_KEY, JSON.stringify([...visibleCartCols]));
    toast.success("Column layout saved");
    setCartColsOpen(false);
  };
  const toggleCartCol = (id) => setVisibleCartCols((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    localStorage.setItem(CART_VISIBLE_COLS_KEY, JSON.stringify([...next]));
    return next;
  });
  const activeCartCols = useMemo(() => cartColOrder.filter((id) => visibleCartCols.has(id)), [visibleCartCols, cartColOrder]);
  const [emailQuoteOpen, setEmailQuoteOpen] = useState(false);
  // iter 128: share dialog state + user list cache
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareData, setShareData] = useState({ shares: [], owner: null });
  const [allUsers, setAllUsers] = useState([]);
  const [shareForm, setShareForm] = useState({ user_id: "", permission: "edit" });
  const loadShareData = useCallback(async () => {
    if (!activeCartId) return;
    try {
      const [shares, users] = await Promise.all([
        api.get(`/carts/${activeCartId}/shares`),
        api.get("/users/searchable"),
      ]);
      setShareData(shares.data);
      setAllUsers(users.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load shares");
    }
  }, [activeCartId]);
  useEffect(() => {
    if (shareDialogOpen) loadShareData();
  }, [shareDialogOpen, loadShareData]);
  const handleAddShare = async () => {
    if (!shareForm.user_id) { toast.error("Pick a user"); return; }
    try {
      await api.put(`/carts/${activeCartId}/shares`, shareForm);
      toast.success("Share saved");
      setShareForm({ user_id: "", permission: "edit" });
      await loadShareData();
      await refreshCarts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save share");
    }
  };
  const handleRemoveShare = async (userId) => {
    try {
      await api.delete(`/carts/${activeCartId}/shares/${userId}`);
      toast.success("Share removed");
      await loadShareData();
      await refreshCarts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove share");
    }
  };
  const [newQuotationOpen, setNewQuotationOpen] = useState(false);
  const [newQuotationName, setNewQuotationName] = useState("");
  const [newQuotationCustomerId, setNewQuotationCustomerId] = useState("unassigned");
  const [newQuotationWarehouse, setNewQuotationWarehouse] = useState(() => localStorage.getItem(LAST_WAREHOUSE_KEY) || null);
  const [editQuotationOpen, setEditQuotationOpen] = useState(false);
  const [editingCart, setEditingCart] = useState(null);
  const [editQuotationName, setEditQuotationName] = useState("");
  const [editQuotationCustomerId, setEditQuotationCustomerId] = useState("unassigned");
  const [listCollapsed, setListCollapsed] = useState(false);
  const [overrideProduct, setOverrideProduct] = useState(null);
  const openOverride = (prod) => { if (canEdit) setOverrideProduct(prod); };
  const [sampleShipOpen, setSampleShipOpen] = useState(false);
  // iter 186 (2026-02): export field settings dialog + selected fields state,
  // loaded lazily on first render from localStorage.
  const [exportFieldsOpen, setExportFieldsOpen] = useState(false);
  const [exportFields, setExportFields] = useState(() => loadExportFields());
  const { widths: colWidths, startResize } = useResizableColumns(CART_COLUMN_WIDTHS_STORAGE_KEY, CART_DEFAULT_COLUMN_WIDTHS, CART_MIN_COLUMN_WIDTHS);

  const fetchItems = useCallback(async () => {
    if (!activeCartId) { setItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/carts/${activeCartId}/items`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load quotation");
    } finally {
      setLoading(false);
    }
  }, [activeCartId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // iter 243 (2026-02): 切换报价单时清空 bulk-select,防止跨单勾选残留。
  useEffect(() => {
    setSelectedProductIds(new Set());
  }, [activeCartId]);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const handleNewQuotation = () => {
    setNewQuotationName("");
    setNewQuotationCustomerId("unassigned");
    setNewQuotationWarehouse(localStorage.getItem(LAST_WAREHOUSE_KEY) || null);
    setNewQuotationOpen(true);
  };

  const handleConfirmNewQuotation = async (e) => {
    e.preventDefault();
    const name = newQuotationName.trim();
    if (!name) return;
    try {
      const cart = await createCart(name, { sample_warehouse_id: newQuotationWarehouse });
      if (newQuotationWarehouse) localStorage.setItem(LAST_WAREHOUSE_KEY, newQuotationWarehouse);
      if (newQuotationCustomerId !== "unassigned") {
        await api.put(`/carts/${cart.id}`, { customer_id: newQuotationCustomerId });
        await refreshCarts();
      }
      toast.success(`Quotation "${name}" created`);
      setNewQuotationOpen(false);
    } catch {
      toast.error("Failed to create quotation");
    }
  };

  const handleRenameQuotation = (cart) => {
    setEditingCart(cart);
    setEditQuotationName(cart.name);
    setEditQuotationCustomerId(cart.customer_id || "unassigned");
    setEditQuotationOpen(true);
  };

  const handleConfirmEditQuotation = async (e) => {
    e.preventDefault();
    const name = editQuotationName.trim();
    if (!name || !editingCart) return;
    try {
      await api.put(`/carts/${editingCart.id}`, {
        name,
        customer_id: editQuotationCustomerId === "unassigned" ? "" : editQuotationCustomerId,
      });
      await refreshCarts();
      if (editingCart.id === activeCartId) fetchItems();
      toast.success("Quotation updated");
      setEditQuotationOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleDeleteQuotation = async (cart) => {
    if (!window.confirm(`Delete quotation "${cart.name}"? This action cannot be undone.`)) return;
    try {
      await deleteCart(cart.id);
      toast.success("Quotation deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleWarehouseChange = async (value) => {
    if (!activeCartId) return;
    try {
      await api.put(`/carts/${activeCartId}`, { sample_warehouse_id: value });
      if (value) localStorage.setItem(LAST_WAREHOUSE_KEY, value);
      toast.success(t("warehouse.updated"));
      refreshCarts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleCustomerChange = async (value) => {
    if (!activeCartId) return;
    try {
      await api.put(`/carts/${activeCartId}`, { customer_id: value === "unassigned" ? "" : value });
      toast.success("Customer linked - currency and coefficient synced");
      fetchItems();
      refreshCarts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const updateItem = async (itemId, payload) => {
    try {
      await api.put(`/carts/${activeCartId}/items/${itemId}`, payload);
      fetchItems();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/carts/${activeCartId}/items/${itemId}`);
      toast.success("Removed");
      fetchItems();
      refreshCarts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to remove");
    }
  };

  // iter 243 (2026-02): 批量删除选中的报价产品(所有 variants 一并
  // 删除)。用 Promise.allSettled 并行 DELETE,允许部分失败,失败的
  // 单条 toast 报错,成功的立即从 UI 消失。删除完清空选择并 refetch。
  const handleBulkDeleteSelected = async () => {
    if (selectedProductIds.size === 0 || !activeCartId) return;
    const itemIdsToDelete = items
      .filter((it) => selectedProductIds.has(it.product_id))
      .map((it) => it.item_id);
    if (itemIdsToDelete.length === 0) return;
    const productCount = selectedProductIds.size;
    if (!window.confirm(`Delete ${productCount} selected product${productCount === 1 ? "" : "s"} (${itemIdsToDelete.length} row${itemIdsToDelete.length === 1 ? "" : "s"}) from this quotation?`)) return;
    // Optimistic: filter locally so UI reflects immediately.
    setItems((prev) => prev.filter((it) => !selectedProductIds.has(it.product_id)));
    const results = await Promise.allSettled(
      itemIdsToDelete.map((id) => api.delete(`/carts/${activeCartId}/items/${id}`))
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      toast.error(`Failed to delete ${failed} item${failed === 1 ? "" : "s"}`);
      fetchItems(); // reconcile to server truth
    } else {
      toast.success(`Deleted ${productCount} product${productCount === 1 ? "" : "s"}`);
    }
    clearProductSelection();
    refreshCarts();
  };

  // iter 134: Persist a new flat order of the quote's cart_items by
  // assigning sequential sort_order values (0..N-1) and PUT-ing to the
  // reorder endpoint. Optimistically updates local `items` so the UI
  // re-renders instantly.
  // iter 237 (2026-02): Removed post-success `fetchItems()` — the server
  // response replaces the whole `items` array which caused image reloads
  // and visible screen jitter right after drop. We already applied the
  // new sort_order optimistically before the PUT, so state and server
  // agree; only refetch when the PUT actually fails, to roll back.
  const sendItemsReorder = async (newFlatItems) => {
    if (!activeCartId || !newFlatItems || newFlatItems.length === 0) return;
    const payload = newFlatItems.map((it, idx) => ({ id: it.item_id, sort_order: idx }));
    setItems(newFlatItems.map((it, idx) => ({ ...it, sort_order: idx })));
    try {
      await api.put(`/carts/${activeCartId}/items/reorder`, payload);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reorder failed");
      fetchItems();
    }
  };

  // Flatten grouped structure back to a linear item list following
  // the current group -> product -> variant visual order.
  const flattenGroups = (grps) => {
    const flat = [];
    grps.forEach((g) => g.products.forEach((p) => p.variants.forEach((v) => flat.push(v))));
    return flat;
  };

  // Move an entire product (all its variants) up/down within its
  // category group. `dir` = -1 (up) or +1 (down).
  const handleMoveProduct = (groupIdx, prodIdx, dir) => {
    const grps = groupItems(items);
    const group = grps[groupIdx];
    if (!group) return;
    const target = prodIdx + dir;
    if (target < 0 || target >= group.products.length) return;
    const products = group.products.slice();
    [products[prodIdx], products[target]] = [products[target], products[prodIdx]];
    group.products = products;
    sendItemsReorder(flattenGroups(grps));
  };

  // Move a single variant up/down within its product's variant list.
  const handleMoveVariant = (groupIdx, prodIdx, variantIdx, dir) => {
    const grps = groupItems(items);
    const product = grps[groupIdx]?.products[prodIdx];
    if (!product) return;
    const target = variantIdx + dir;
    if (target < 0 || target >= product.variants.length) return;
    const variants = product.variants.slice();
    [variants[variantIdx], variants[target]] = [variants[target], variants[variantIdx]];
    product.variants = variants;
    sendItemsReorder(flattenGroups(grps));
  };

  // iter 235 (2026-02): 智能排序 —— 按类别/子类别聚合 + 组内按最低价升序。
  // 组内的产品:按 min(variant.final_price ?? variant.price) 升序。
  // 组的排列:按 category name(A→Z)。variants 顺序保留当前(即 groupByProduct
  // 里 push 的顺序,通常是 size 数字升序或 FM 原序)。持久化走既有 reorder API。
  const handleSmartSort = () => {
    if (!items || items.length === 0) return;
    const grps = groupItems(items);
    const priceOf = (p) => {
      const prices = (p.variants || []).map((v) => v.final_price ?? v.price).filter((x) => x != null && !Number.isNaN(Number(x)));
      return prices.length ? Math.min(...prices.map(Number)) : Number.POSITIVE_INFINITY;
    };
    grps.forEach((g) => {
      g.products = (g.products || []).slice().sort((a, b) => priceOf(a) - priceOf(b));
    });
    const sortedGroups = grps.slice().sort((a, b) => (a.key || "").localeCompare(b.key || ""));
    sendItemsReorder(flattenGroups(sortedGroups));
    toast.success("已按类别 + 价格智能排序");
  };

  // iter 236 (2026-02): 手动排序改成 HTML5 原生拖拽。用户拖动一个产品块
  // (list view 里的整行,grid view 里的整张卡片)到另一个产品位置,产品
  // 位置对调。跨类别 / 跨子类别拖拽也允许(如果目标在其它 group,插入到
  // 目标所在组的对应位置)。sendItemsReorder 直接持久化新的 sort_order。
  const [dragProductId, setDragProductId] = useState(null);
  const handleProductDrop = (targetProductId) => {
    if (!dragProductId || !targetProductId || dragProductId === targetProductId) {
      setDragProductId(null);
      return;
    }
    const grps = groupItems(items);
    // Find src (group idx, product idx) and target similarly
    let srcGI = -1, srcPI = -1, tgtGI = -1, tgtPI = -1;
    for (let gi = 0; gi < grps.length; gi++) {
      for (let pi = 0; pi < (grps[gi].products || []).length; pi++) {
        if (grps[gi].products[pi].product_id === dragProductId) { srcGI = gi; srcPI = pi; }
        if (grps[gi].products[pi].product_id === targetProductId) { tgtGI = gi; tgtPI = pi; }
      }
    }
    if (srcGI < 0 || tgtGI < 0) { setDragProductId(null); return; }
    const [moved] = grps[srcGI].products.splice(srcPI, 1);
    // Adjust target index if source was before target in same group (post-splice).
    const insertAt = (srcGI === tgtGI && srcPI < tgtPI) ? tgtPI - 1 : tgtPI;
    grps[tgtGI].products.splice(insertAt, 0, moved);
    sendItemsReorder(flattenGroups(grps));
    setDragProductId(null);
  };

  const comingSoon = (label) => () => toast.info(`${label} is coming soon, pending format template confirmation`);

  const handleExportPdf = async () => {
    if (items.length === 0) { toast.error("Quotation is empty"); return; }
    toast.info("Generating PDF...");
    try {
      const quotedItems = items.map((it) => ({ ...it, price: it.final_price }));
      await exportQuotationPdf(quotedItems, `quotation-${activeCart?.name || "cart"}`, activeCart?.currency, exportFields);
      toast.success("PDF exported successfully");
    } catch {
      toast.error("PDF export failed");
    }
  };

  const handleExportExcel = async () => {
    if (items.length === 0) { toast.error("Quotation is empty"); return; }
    toast.info("Generating Excel...");
    try {
      const quotedItems = items.map((it) => ({ ...it, price: it.final_price }));
      await exportQuotationExcel(quotedItems, `quotation-${activeCart?.name || "cart"}`, activeCart?.currency, exportFields);
      toast.success("Excel exported successfully");
    } catch {
      toast.error("Excel export failed");
    }
  };

  const groups = useMemo(() => groupItems(items), [items]);
  // iter 230 (2026-02): 扁平化产品列表(保持 groups → products 原始顺序)供
  // Edit Quote Item Dialog 内 Prev/Next 导航使用。
  const flatProducts = useMemo(() => groups.flatMap((g) => g.products || []), [groups]);

  return (
    <AppLayout>
      {/* iter 214 (2026-02): 方案 E —— 报价单顶部菜单压成 1 行。
          - 原"页面标题栏"(Quotations + Export Field Settings + New)删除:
            "Quotations"标题冗余(左侧列表面板已有);"New"移到左侧面板顶部
            已有按钮;"Export Field Settings"移入 Actions 下拉菜单。
          - 原"标题/参数栏 + 工具栏"两行合并为 1 行 flex-wrap:
            title + 日期badge + customer + 6 个参数胶囊 + [ml-auto] + Actions/
            Select Products/视图/Columns/Fullscreen。 */}
      <div data-testid="cart-page" className="flex gap-3 items-stretch max-w-screen-2xl min-h-[calc((100vh-64px)*0.7225)]">
        <QuotationList
          carts={carts}
          activeCartId={activeCartId}
          onSelect={setActiveCartId}
          onCreate={handleNewQuotation}
          onRename={handleRenameQuotation}
          onDelete={handleDeleteQuotation}
          onDuplicate={duplicateCart}
          onShare={(c) => { setActiveCartId(c.id); setShareDialogOpen(true); }}
          collapsed={listCollapsed}
          onToggleCollapse={() => setListCollapsed((c) => !c)}
        />

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          {!activeCart ? (
            <div className="bg-white border border-gray-200 rounded-xl py-10 text-center" data-testid="quotation-detail-empty">
              <p className="text-sm text-gray-400">{t("cart.select_quotation_empty")}</p>
            </div>
          ) : (
            <>
              {/* iter 282: read-only banner for other people's quotations. */}
              {!canEdit && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="cart-readonly-banner">
                  <Lock size={12} strokeWidth={2} className="flex-shrink-0" />
                  <span className="font-semibold">{t("cart.readonly")}</span>
                  {activeCart.owner?.name && <span>· {t("cart.owner_label")} {activeCart.owner.name}</span>}
                  <span className="text-amber-700/80">· {t("cart.readonly_hint")}</span>
                  <button
                    type="button"
                    onClick={() => duplicateCart(activeCart)}
                    data-testid="cart-copy-as-mine-button"
                    className="ml-auto inline-flex items-center gap-1 h-6 px-2 rounded-md bg-white border border-amber-300 text-amber-900 font-medium hover:bg-amber-100 transition-colors"
                  >
                    <Copy size={11} strokeWidth={2} /> {t("cart.copy_as_mine")}
                  </button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="quotation-detail-toolbar">
                <div className="flex items-baseline gap-1.5 min-w-0">
                  <h1 className="font-heading text-sm font-bold tracking-tight text-gray-900 truncate" data-testid="quotation-detail-title">{activeCart.name}</h1>
                  <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap" data-testid="quotation-detail-subtitle">
                    {activeCart.created_at ? new Date(activeCart.created_at).toLocaleDateString("en-US") : "-"}
                  </span>
                  {activeCart.my_permission === "read" && (
                    <span className="inline-flex items-center px-1.5 py-0 rounded-full bg-amber-100 text-amber-800 text-[9px] font-medium whitespace-nowrap" data-testid="cart-readonly-badge">
                      Read-only
                    </span>
                  )}
                  {activeCart.my_permission === "edit" && (
                    <span className="inline-flex items-center px-1.5 py-0 rounded-full bg-blue-100 text-blue-800 text-[9px] font-medium whitespace-nowrap" data-testid="cart-editable-shared-badge">
                      Edit-shared
                    </span>
                  )}
                </div>

                {/* iter 275: 报价单所属样品仓库;有出库记录后锁定。 */}
                <div className="flex items-center gap-1" data-testid="cart-warehouse-block">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">{t("warehouse.label")}</span>
                  <WarehouseSelect value={activeCart.sample_warehouse_id} onChange={handleWarehouseChange}
                    disabled={activeCart.samples_locked || !canEdit} testId="cart-warehouse-select" className="w-32 h-6 text-[11px]" />
                  {activeCart.samples_locked && <span className="text-[9px] text-gray-400" data-testid="cart-warehouse-locked">{t("warehouse.locked")}</span>}
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">{t("cart.customer")}</span>
                  <Select value={activeCart.customer_id || "unassigned"} onValueChange={handleCustomerChange} disabled={!canEdit}>
                    <SelectTrigger data-testid="cart-customer-select" className="w-32 h-6 rounded-md border-gray-300 text-[11px] focus:ring-brand-500">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg">
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500 font-bold">{t("cart.quot_rate")}</span>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    disabled={!canEdit}
                    data-testid="cart-quotation-rate-input"
                    className="w-14 h-6 rounded-md border border-gray-300 text-[11px] font-mono text-center focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500"
                    defaultValue={activeCart.quotation_rate ?? ""}
                    placeholder="auto"
                    onBlur={async (e) => {
                      const v = e.target.value === "" ? null : Number(e.target.value);
                      if (v === activeCart.quotation_rate) return;
                      try {
                        await api.put(`/carts/${activeCart.id}`, { quotation_rate: v });
                        toast.success("Quotation rate updated");
                        await refreshCarts();
                        fetchItems(activeCart.id);
                      } catch (err) {
                        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
                      }
                    }}
                  />
                </div>
                <div className="flex items-center gap-1.5 px-2 h-6 rounded-md bg-gray-50 border border-gray-200 font-mono text-[11px] text-gray-700" data-testid="cart-readonly-params">
                  <span><span className="text-gray-500">{t("cart.coeff")}</span> <span data-testid="cart-price-coefficient-display" className="font-semibold text-gray-900">{activeCart.price_coefficient ?? "-"}</span></span>
                  <span className="text-gray-300">·</span>
                  <span data-testid="cart-currency-display" className="font-semibold text-gray-900">{activeCart.currency || "USD"}</span>
                  <span className="text-gray-300">·</span>
                  <span><span className="text-gray-500">USD:RMB</span> <span className="font-semibold text-gray-900">{activeCart.effective_usd_to_rmb ?? "-"}</span></span>
                  <span className="text-gray-300">·</span>
                  <span><span className="text-gray-500">USD:CAD</span> <span className="font-semibold text-gray-900">{activeCart.effective_usd_to_cad ?? "-"}</span></span>
                  <span className="text-gray-300">·</span>
                  <span><span className="text-gray-500">{t("cart.sea_freight")}</span> <span className="font-semibold text-gray-900">{activeCart.effective_sea_freight != null ? `$${activeCart.effective_sea_freight}` : "-"}</span></span>
                </div>

                {/* iter 214 (2026-02): 原独立"工具栏行"合并进这条 flex-wrap;
                    第一个按钮 ml-auto 使其在宽屏时靠右,窄屏 flex-wrap 自动换行。 */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="ml-auto rounded-md bg-gray-900 hover:bg-gray-800 text-[11px] h-7 px-2.5" data-testid="cart-actions-button">
                      {t("common.actions")} <ChevronDown size={12} strokeWidth={1.75} className="ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="rounded-lg w-48">
                    {canShareExport && (
                      <DropdownMenuItem onClick={() => setEmailQuoteOpen(true)} data-testid="cart-email-quote-button" className="cursor-pointer">
                        <Mail size={14} strokeWidth={1.75} className="mr-2" /> Email Quote
                      </DropdownMenuItem>
                    )}
                    {canEdit && <DropdownMenuItem onClick={() => setSampleShipOpen(true)} data-testid="cart-ship-samples-button" className="text-xs">{t("samples.ship_action")}</DropdownMenuItem>}
                    {canShareExport && (
                      <>
                        <DropdownMenuItem onClick={handleExportExcel} data-testid="cart-export-excel-button" className="cursor-pointer">
                          <FileSpreadsheet size={14} strokeWidth={1.75} className="mr-2" /> Export Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleExportPdf} data-testid="cart-export-pdf-button" className="cursor-pointer">
                          <FileText size={14} strokeWidth={1.75} className="mr-2" /> Export PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={comingSoon("Print Label")} data-testid="cart-print-label-button" className="cursor-pointer">
                          <FileOutput size={14} strokeWidth={1.75} className="mr-2" /> Print Label
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setExportFieldsOpen(true)} data-testid="cart-export-fields-settings" className="cursor-pointer">
                          <ColumnsIcon size={14} strokeWidth={1.75} className="mr-2" /> {t("cart.export_fields_settings")}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem
                      data-testid="cart-duplicate-button"
                      className="cursor-pointer"
                      onClick={() => duplicateCart(activeCart)}
                    >
                      <Copy size={14} strokeWidth={1.75} className="mr-2" /> {canEdit ? "Duplicate Quotation" : t("cart.copy_as_mine")}
                    </DropdownMenuItem>
                    {/* iter 128: share this quotation with edit / read-only permission.
                        Show for owner (backend enforces on save) OR when
                        my_permission is undefined (legacy quotes without
                        owner_id - treated as owner by backend). */}
                    {(activeCart.my_permission === "owner" || activeCart.my_permission == null) && (
                      <DropdownMenuItem onClick={() => setShareDialogOpen(true)} data-testid="cart-share-button" className="cursor-pointer">
                        <Users size={14} strokeWidth={1.75} className="mr-2" /> Share Quotation
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {canEdit && (
                  <Button onClick={() => navigate("/")} data-testid="cart-select-products-button" className="rounded-md bg-[#1677ff] hover:bg-[#0958d9] text-[11px] h-7 px-2.5">
                    <PackageSearch size={12} className="mr-1" /> {t("cart.select_products")}
                  </Button>
                )}

                {canEdit && (
                <button
                  data-testid="cart-smart-sort-button"
                  onClick={handleSmartSort}
                  title="Smart Sort: group by category, sort by price ascending"
                  className="h-7 px-2 rounded-md border border-gray-200 flex items-center gap-1 text-[11px] text-gray-700 hover:border-brand-300 transition-colors bg-white"
                >
                  <ArrowUpDown size={11} strokeWidth={1.75} className="text-gray-400" />
                  <span>智能排序</span>
                </button>
                )}

                <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
                  <button
                    data-testid="quotation-view-toggle-list"
                    onClick={() => setItemView("list")}
                    className={`w-7 h-7 flex items-center justify-center transition-colors ${itemView === "list" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                  >
                    <ListIcon size={13} strokeWidth={1.75} />
                  </button>
                  <button
                    data-testid="quotation-view-toggle-grid"
                    onClick={() => setItemView("grid")}
                    className={`w-7 h-7 flex items-center justify-center transition-colors ${itemView === "grid" ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                  >
                    <LayoutGrid size={13} strokeWidth={1.75} />
                  </button>
                </div>

                {/* iter 237 (2026-02): 大 / 中 / 小 三档卡片尺寸,仅 grid view 显示。
                    中 / 小 只显示图 + 编号 + 描述,不显示价格,便于快速浏览。 */}
                {itemView === "grid" && (
                  <div className="flex items-center border border-gray-200 rounded-md overflow-hidden" data-testid="grid-size-toolbar">
                    {["L", "M", "S"].map((s) => (
                      <button
                        key={s}
                        data-testid={`grid-size-${s.toLowerCase()}`}
                        onClick={() => setGridSize(s)}
                        title={s === "L" ? "Large" : s === "M" ? "Medium" : "Small"}
                        className={`w-7 h-7 flex items-center justify-center text-[11px] font-semibold transition-colors ${gridSize === s ? "bg-brand-600 text-white" : "text-gray-400 hover:bg-gray-50"}`}
                      >
                        {s === "L" ? "大" : s === "M" ? "中" : "小"}
                      </button>
                    ))}
                  </div>
                )}

                {itemView === "list" && (
                  <button
                    data-testid="cart-columns-button"
                    onClick={() => setCartColsOpen(true)}
                    className="h-7 px-2 rounded-md border border-gray-200 flex items-center gap-1 text-[11px] text-gray-700 hover:border-brand-300 transition-colors bg-white"
                  >
                    <ColumnsIcon size={11} strokeWidth={1.75} className="text-gray-400" />
                    <span>{t("dashboard.columns")}</span>
                    <span className="text-gray-400 font-mono">{activeCartCols.length}</span>
                  </button>
                )}

                {/* iter 207: fullscreen toggle for the cart items table */}
                <button
                  data-testid="cart-fullscreen-toggle"
                  onClick={toggleCartFullscreen}
                  title={cartFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                  className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  {cartFullscreen ? <Minimize2 size={12} strokeWidth={1.75} /> : <Maximize2 size={12} strokeWidth={1.75} />}
                </button>
              </div>

              {/* iter 243 (2026-02): 批量选择动作条。仅当有产品被勾选时显示,
                  展示已选数量 + 全选/清空 + 删除按钮。放在 toolbar 与列表之间,
                  滑入式浮层不会挤动主视图布局。 */}
              {canEdit && items.length > 0 && (() => {
                const uniqueProductIds = Array.from(new Set(items.map((it) => it.product_id)));
                const allSelected = uniqueProductIds.length > 0 && uniqueProductIds.every((pid) => selectedProductIds.has(pid));
                const someSelected = selectedProductIds.size > 0 && !allSelected;
                return (
                  <div
                    className={`flex items-center gap-3 px-3 py-1.5 rounded-md text-[11px] transition-all ${
                      selectedProductIds.size > 0
                        ? "bg-brand-50 border border-brand-200 text-brand-800"
                        : "bg-gray-50 border border-gray-200 text-gray-600"
                    }`}
                    data-testid="cart-bulk-select-bar"
                  >
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={(v) => {
                          if (v) setSelectedProductIds(new Set(uniqueProductIds));
                          else clearProductSelection();
                        }}
                        data-testid="cart-bulk-select-all"
                      />
                      <span>Select all</span>
                    </label>
                    <span className="text-gray-400">·</span>
                    <span data-testid="cart-bulk-selected-count">
                      {selectedProductIds.size > 0
                        ? `${selectedProductIds.size} product${selectedProductIds.size === 1 ? "" : "s"} selected`
                        : "No selection"}
                    </span>
                    {selectedProductIds.size > 0 && (
                      <>
                        <button
                          onClick={clearProductSelection}
                          className="text-brand-700 hover:text-brand-900 underline underline-offset-2"
                          data-testid="cart-bulk-clear"
                        >
                          Clear
                        </button>
                        <span className="ml-auto flex items-center gap-2">
                          <button
                            onClick={handleBulkDeleteSelected}
                            className="h-6 px-2.5 rounded-md bg-red-600 hover:bg-red-700 text-white flex items-center gap-1 font-medium transition-colors"
                            data-testid="cart-bulk-delete-button"
                          >
                            <Trash2 size={11} strokeWidth={2} />
                            Delete Selected
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                );
              })()}
              {loading ? (
                <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-sm text-gray-400 flex-1" data-testid="cart-loading">Loading...</div>
              ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl py-10 text-center flex-1" data-testid="cart-empty-state">
                  <p className="text-sm text-gray-400">Quotation is empty. Click "Select Products" to add items from the product list.</p>
                </div>
              ) : itemView === "grid" ? (
                /* iter 236 (2026-02): Grid view 现在按产品聚合(和 list view 一致),
                   每张卡片显示产品的所有 variants。HTML5 native drag 支持在 grid
                   里也能拖拽,和 list view 用同一个 handleProductDrop。
                   iter 237 (2026-02): 大/中/小 三档尺寸;中/小 不显示价格;点击
                   产品编号(item_no)可打开 Edit Quote Item Dialog。 */
                <div
                  className={
                    gridSize === "S"
                      ? "grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 flex-1 content-start"
                      : gridSize === "M"
                        ? "grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 flex-1 content-start"
                        : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 content-start"
                  }
                  data-testid="cart-items-grid-container"
                  data-grid-size={gridSize}
                >
                  {groups.flatMap((g) => (g.products || []).map((prod) => {
                    const showPrice = gridSize === "L";
                    const photoCls = gridSize === "S" ? "w-full aspect-square" : gridSize === "M" ? "w-16 h-16" : "w-20 h-20";
                    return (
                      <div
                        key={prod.product_id}
                        data-testid={`cart-grid-product-${prod.product_id}`}
                        draggable={canEdit}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", prod.product_id);
                          setDragProductId(prod.product_id);
                        }}
                        onDragOver={(e) => {
                          if (!dragProductId || dragProductId === prod.product_id) return;
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleProductDrop(prod.product_id);
                        }}
                        onDragEnd={() => setDragProductId(null)}
                        className={`relative bg-white border rounded-xl cursor-grab active:cursor-grabbing transition-all ${
                          gridSize === "S" ? "p-2 space-y-1" : gridSize === "M" ? "p-3 space-y-1.5" : "p-4 space-y-2"
                        } ${
                          selectedProductIds.has(prod.product_id)
                            ? "border-brand-500 ring-1 ring-brand-500"
                            : "border-gray-200 hover:border-brand-300 hover:shadow-sm"
                        } ${dragProductId === prod.product_id ? "opacity-40" : ""}`}
                      >
                        {/* iter 243 (2026-02): Grid 卡片左上角 checkbox 覆盖层。 */}
                        {canEdit && (
                        <label
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          draggable={false}
                          onDragStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                          className={`absolute top-1.5 left-1.5 z-10 flex items-center justify-center w-5 h-5 rounded bg-white/90 backdrop-blur-sm border shadow-sm cursor-pointer transition-colors ${
                            selectedProductIds.has(prod.product_id)
                              ? "border-brand-500 ring-1 ring-brand-500"
                              : "border-gray-300 hover:border-brand-400"
                          }`}
                        >
                          <Checkbox
                            checked={selectedProductIds.has(prod.product_id)}
                            onCheckedChange={() => toggleProductSelected(prod.product_id)}
                            className="h-3.5 w-3.5"
                            data-testid={`cart-grid-select-product-${prod.product_id}`}
                          />
                        </label>
                        )}
                        {gridSize === "S" ? (
                          <>
                            {prod.photo_url ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setLightbox({ open: true, src: prod.photo_url, alt: `${prod.item_no || ""}  ${prod.description || ""}`.trim() }); }}
                                data-testid={`cart-grid-photo-lightbox-${prod.product_id}`}
                                className={`p-0 border-0 bg-transparent cursor-zoom-in transition-transform hover:scale-105 block ${photoCls}`}
                                title="Click to zoom"
                              >
                                <img src={prod.photo_url} alt={prod.item_no} className="w-full h-full object-contain rounded-md" />
                              </button>
                            ) : (
                              <div className={`${photoCls} bg-gray-50 rounded-md`} />
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openOverride(prod); }}
                              data-testid={`cart-grid-item-no-${prod.product_id}`}
                              className="font-mono text-[10px] font-bold text-brand-700 hover:text-brand-800 hover:underline block truncate max-w-full text-left"
                              title="Edit product details"
                            >
                              {prod.item_no}
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="flex gap-2">
                              <GripVertical size={14} className="text-gray-300 flex-shrink-0 mt-1" />
                              {prod.photo_url ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setLightbox({ open: true, src: prod.photo_url, alt: `${prod.item_no || ""}  ${prod.description || ""}`.trim() }); }}
                                  data-testid={`cart-grid-photo-lightbox-${prod.product_id}`}
                                  className="p-0 border-0 bg-transparent cursor-zoom-in transition-transform hover:scale-105 flex-shrink-0"
                                  title="Click to zoom"
                                >
                                  <img src={prod.photo_url} alt={prod.item_no} className={`${photoCls} object-contain rounded-lg`} />
                                </button>
                              ) : (
                                <div className={`${photoCls} bg-gray-50 rounded-lg flex-shrink-0`} />
                              )}
                              <div className="min-w-0 flex-1">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); openOverride(prod); }}
                                  data-testid={`cart-grid-item-no-${prod.product_id}`}
                                  className="font-mono text-xs font-bold text-brand-700 hover:text-brand-800 hover:underline block text-left"
                                  title="Edit product details"
                                >
                                  {prod.item_no}
                                </button>
                                <p className={`text-gray-700 ${gridSize === "M" ? "text-xs line-clamp-1" : "text-sm line-clamp-2"}`}>{prod.description}</p>
                                {gridSize === "L" && (
                                  <p className="text-[10px] text-gray-400 line-clamp-1">{prod.specification || "-"}</p>
                                )}
                              </div>
                            </div>
                            {/* Variants list — 只在 L 时显示价格分行 */}
                            {showPrice && (
                              <div className="border-t border-gray-100 pt-2 space-y-1">
                                {prod.variants.map((v) => (
                                  <div key={v.item_id} className="flex items-center justify-between text-xs" data-testid={`cart-grid-variant-${v.item_id}`}>
                                    <span className="text-gray-600 font-mono">{v.size || "-"}</span>
                                    <div className="flex items-center gap-3">
                                      <span className="text-gray-400">×{v.qty ?? "-"}</span>
                                      <span className="font-mono font-bold text-brand-600 w-16 text-right">{formatCurrency(v.final_price ?? v.price, activeCart.currency)}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  }))}
                </div>
              ) : (
                <div
                  className={cartFullscreen ? "fixed inset-0 z-50 bg-white p-3 overflow-hidden" : "flex-1 min-h-0 flex flex-col"}
                  data-testid={cartFullscreen ? "cart-fullscreen-wrapper" : undefined}
                >
                  {cartFullscreen && (
                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                      <h2 className="font-heading text-sm font-bold text-gray-900" data-testid="cart-fullscreen-title">{activeCart.name}</h2>
                      <button
                        data-testid="cart-fullscreen-exit"
                        onClick={toggleCartFullscreen}
                        className="h-7 px-2 flex items-center gap-1 rounded-md border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50"
                        title="Exit fullscreen (Esc)"
                      >
                        <Minimize2 size={12} strokeWidth={1.75} /> Exit fullscreen
                      </button>
                    </div>
                  )}
                  <div
                    className={`bg-white border border-gray-300 [&_table]:text-[12px] [&_th]:!h-8 [&_th]:!px-1.5 [&_th]:!py-1 [&_th]:!bg-gray-100 [&_th]:!text-gray-700 [&_th]:!font-semibold [&_th]:!border-r [&_th]:!border-gray-300 [&_th]:!leading-tight [&_th:last-child]:!border-r-0 [&_td]:!p-1 [&_td]:!border-r [&_td]:!border-gray-200 [&_td]:!leading-tight [&_td:not([data-photo-cell])]:!align-top [&_td[data-photo-cell]]:!align-middle [&_td:last-child]:!border-r-0 [&_tr]:!border-b [&_tr]:!border-gray-300 [&_tr:not(.row-selected):hover]:!bg-blue-50/40 [&_td]:!font-semibold [&_td_.text-gray-500]:!text-gray-900 [&_td_.text-gray-400]:!text-gray-900 [&_td_.text-gray-700]:!text-gray-900 [&_td_.text-gray-800]:!text-gray-900 [&_td_input]:!font-bold [&_td_input]:!text-gray-900 [&_div.relative.w-full.overflow-auto]:!overflow-visible ${cartFullscreen ? "overflow-auto always-scroll" : "overflow-hidden flex-1 min-h-0 flex flex-col"}`}
                    style={cartFullscreen ? { maxHeight: "calc(100vh - 60px)" } : undefined}
                    data-testid="cart-items-table-container"
                  >
                  {/* iter 219 (2026-02):
                      • 全屏 —— 容器本身开 overflow-auto + max-height = viewport - header。
                        行少时高度贴合内容(消除底部大片空白);行多时容器封顶,滚动条落在
                        容器底部 = 视窗底部附近,不用垂直滚到最底。
                      • iter 219b (2026-02) —— shadcn `<Table>` 内部**自带**一层
                        `w-full overflow-auto`,那才是真正的横向滚动元素,它的滚动条会出现
                        在表格自身的底部(行少时 = 页面中间)。用 arbitrary variant
                        `[&_div.relative.w-full.overflow-auto]:!overflow-visible` 覆盖它,
                        让横向滚动交给外层容器 → 滚动条固定在容器底部 = 视窗底部。
                      • iter 219c (2026-02) —— 上述 override 全屏 / 非全屏都应用,让**非全屏**
                        模式里那个 max-h-[68vh] 内层 scroller 也接过横向滚动 → 滚动条钉在
                        68vh 容器底部,非全屏也不用垂直滚到最底。 */}
                  <div className={cartFullscreen ? "" : "overflow-auto always-scroll flex-1 min-h-0"}>
                    <Table className="text-[14px]" style={{ tableLayout: "fixed", width: activeCartCols.reduce((sum, id) => sum + colWidths[id], 0) }}>
                      <colgroup>
                        {activeCartCols.map((id) => <col key={id} style={{ width: colWidths[id] }} />)}
                      </colgroup>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-200">
                          {visibleCartCols.has("photo") && <ResizableTh id="photo" width={colWidths.photo} onResizeStart={startResize("photo")}>{t("dashboard.columns_labels.photo")}</ResizableTh>}
                          {visibleCartCols.has("description") && <ResizableTh id="description" width={colWidths.description} onResizeStart={startResize("description")}>{t("dashboard.columns_labels.description")}</ResizableTh>}
                          {visibleCartCols.has("material") && <ResizableTh id="material" width={colWidths.material} onResizeStart={startResize("material")}>{t("dashboard.columns_labels.material")}</ResizableTh>}
                          {visibleCartCols.has("category") && <ResizableTh id="category" width={colWidths.category} onResizeStart={startResize("category")}>{t("dashboard.columns_labels.category")}</ResizableTh>}
                          {visibleCartCols.has("bulky") && <ResizableTh id="bulky" width={colWidths.bulky} onResizeStart={startResize("bulky")}>{t("dashboard.columns_labels.bulky")}</ResizableTh>}
                          {visibleCartCols.has("size") && <ResizableTh id="size" width={colWidths.size} onResizeStart={startResize("size")}>{t("dashboard.columns_labels.size")}</ResizableTh>}
                          {visibleCartCols.has("pcs_ctn") && <ResizableTh id="pcs_ctn" width={colWidths.pcs_ctn} align="right" onResizeStart={startResize("pcs_ctn")}>{t("dashboard.columns_labels.pcs_ctn_cart")}</ResizableTh>}
                          {visibleCartCols.has("ci_qty") && <ResizableTh id="ci_qty" width={colWidths.ci_qty} align="right" onResizeStart={startResize("ci_qty")} title="FileMaker inventory snapshot - not included in exports">{t("dashboard.columns_labels.ci_qty")}</ResizableTh>}
                          {visibleCartCols.has("gt_sku") && <ResizableTh id="gt_sku" width={colWidths.gt_sku} onResizeStart={startResize("gt_sku")}>{t("dashboard.columns_labels.gt_sku")}</ResizableTh>}
                          {visibleCartCols.has("barcode") && <ResizableTh id="barcode" width={colWidths.barcode} onResizeStart={startResize("barcode")}>{t("dashboard.columns_labels.barcode")}</ResizableTh>}
                          {visibleCartCols.has("carton_dims") && <ResizableTh id="carton_dims" width={colWidths.carton_dims} align="right" onResizeStart={startResize("carton_dims")}>{t("dashboard.columns_labels.carton_dims")}</ResizableTh>}
                          {visibleCartCols.has("qty_40ft") && <ResizableTh id="qty_40ft" width={colWidths.qty_40ft} align="right" onResizeStart={startResize("qty_40ft")}>{t("dashboard.columns_labels.qty_40ft")}</ResizableTh>}
                          {visibleCartCols.has("gross_weight") && <ResizableTh id="gross_weight" width={colWidths.gross_weight} align="right" onResizeStart={startResize("gross_weight")}>{t("dashboard.columns_labels.gross_weight")}</ResizableTh>}
                          {visibleCartCols.has("purchase_price") && <ResizableTh id="purchase_price" width={colWidths.purchase_price} align="right" onResizeStart={startResize("purchase_price")}>{t("dashboard.columns_labels.purchase_price")}</ResizableTh>}
                          {visibleCartCols.has("customs_duty") && <ResizableTh id="customs_duty" width={colWidths.customs_duty} align="right" onResizeStart={startResize("customs_duty")}>{t("dashboard.columns_labels.customs_duty")}</ResizableTh>}
                          {visibleCartCols.has("tax_rebate") && <ResizableTh id="tax_rebate" width={colWidths.tax_rebate} align="right" onResizeStart={startResize("tax_rebate")}>{t("dashboard.columns_labels.tax_rebate")}</ResizableTh>}
                          {visibleCartCols.has("price") && <ResizableTh id="price" width={colWidths.price} align="right" onResizeStart={startResize("price")}>{t("dashboard.columns_labels.price")}</ResizableTh>}
                          {visibleCartCols.has("final_price") && <ResizableTh id="final_price" width={colWidths.final_price} align="right" onResizeStart={startResize("final_price")}>{t("dashboard.columns_labels.final_price")}</ResizableTh>}
                          {visibleCartCols.has("actions") && <ResizableTh id="actions" width={colWidths.actions} onResizeStart={startResize("actions")}></ResizableTh>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groups.map((group, gIdx) => (
                          <React.Fragment key={group.key}>
                            <TableRow className="hover:bg-transparent bg-gray-50/70 border-b border-gray-100">
                              <TableCell colSpan={activeCartCols.length} className="text-xs font-semibold uppercase tracking-label text-gray-500 py-2" data-testid={`cart-group-header-${group.key}`}>
                                {group.key}
                              </TableCell>
                            </TableRow>
                            {group.products.map((prod, pIdx) => (
                              <React.Fragment key={prod.product_id}>
                                {prod.variants.map((item, vIdx) => (
                                  <TableRow
                                    key={item.item_id}
                                    data-testid={`cart-item-row-${item.item_id}`}
                                    draggable={canEdit && vIdx === 0}
                                    onDragStart={(e) => {
                                      if (vIdx !== 0) return;
                                      e.dataTransfer.effectAllowed = "move";
                                      e.dataTransfer.setData("text/plain", prod.product_id);
                                      setDragProductId(prod.product_id);
                                    }}
                                    onDragOver={(e) => {
                                      if (!dragProductId || dragProductId === prod.product_id) return;
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = "move";
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      handleProductDrop(prod.product_id);
                                    }}
                                    onDragEnd={() => setDragProductId(null)}
                                    onClick={(e) => {
                                      // Ignore clicks that came from an
                                      // interactive control inside the row
                                      // (buttons, inputs, links). Those have
                                      // their own semantics.
                                      const target = e.target;
                                      if (target.closest("button, input, a, [role=button]")) return;
                                      setSelectedQuoteProductId((cur) => cur === prod.product_id ? null : prod.product_id);
                                    }}
                                    className={`border-b border-gray-100 cursor-pointer ${
                                      selectedQuoteProductId === prod.product_id
                                        ? "row-selected !bg-amber-50 hover:!bg-amber-100"
                                        : ""
                                    } ${dragProductId === prod.product_id && vIdx === 0 ? "opacity-40" : ""}`}
                                  >
                                    {vIdx === 0 && (
                                      <>
                                        {visibleCartCols.has("photo") && (
                                        <TableCell
                                          rowSpan={prod.variants.length}
                                          data-photo-cell
                                          className="py-2 text-center relative"
                                        >
                                          {/* iter 243 (2026-02): 图片左上角浮动 checkbox —— 常驻显示,
                                              一眼看得到"哪些能勾"。选中时高亮描边;停止事件冒泡
                                              避免触发外层 row-click 高亮。 */}
                                          {canEdit && (
                                          <label
                                            onClick={(e) => e.stopPropagation()}
                                            className={`absolute top-1 left-1 z-10 flex items-center justify-center w-5 h-5 rounded bg-white/90 backdrop-blur-sm border shadow-sm cursor-pointer transition-colors ${
                                              selectedProductIds.has(prod.product_id)
                                                ? "border-brand-500 ring-1 ring-brand-500"
                                                : "border-gray-300 hover:border-brand-400"
                                            }`}
                                          >
                                            <Checkbox
                                              checked={selectedProductIds.has(prod.product_id)}
                                              onCheckedChange={() => toggleProductSelected(prod.product_id)}
                                              className="h-3.5 w-3.5"
                                              data-testid={`cart-select-product-${prod.product_id}`}
                                            />
                                          </label>
                                          )}
                                          {/* iter 219 (2026-02): 父容器针对 td[data-photo-cell] 用
                                              `!align-middle` 单独放行,其它 td 仍是 !align-top。
                                              object-contain 让整张图完整可见(不被裁剪)。
                                              iter 220 (2026-02): 点缩略图弹全屏 lightbox 看细节。
                                              iter 229 (2026-02): 图片放大到 w-28 h-28,与 Dashboard
                                              产品清单默认(w-28 h-28)一致。 */}
                                          {item.photo_url ? (
                                            <button
                                              type="button"
                                              onClick={() => setLightbox({ open: true, src: item.photo_url, alt: `${item.item_no || ""}  ${item.description || ""}`.trim() })}
                                              data-testid={`cart-photo-lightbox-open-${item.item_no}`}
                                              className="p-0 border-0 bg-transparent cursor-zoom-in transition-transform hover:scale-105"
                                              title="Click to zoom"
                                            >
                                              <img src={item.photo_url} alt={item.item_no} className="w-28 h-28 object-contain rounded-lg inline-block" />
                                            </button>
                                          ) : (
                                            <div className="w-28 h-28 bg-gray-50 rounded-lg inline-block" />
                                          )}
                                        </TableCell>
                                        )}
                                        {visibleCartCols.has("description") && (
                                        <TableCell rowSpan={prod.variants.length} className="text-sm text-gray-700 align-top overflow-hidden">
                                          <div className="flex items-start gap-1.5">
                                            <div className="min-w-0">
                                              {/* iter 184 (2026-02): 产品编号 (item_no) is now
                                                  itself the clickable trigger for the override
                                                  dialog. Keeps the pencil icon as a redundant
                                                  affordance so existing muscle memory / tests
                                                  still work. */}
                                              <button
                                                type="button"
                                                onClick={() => openOverride(prod)}
                                                title="Edit for this quote only"
                                                data-testid={`cart-item-no-open-override-${prod.product_id}`}
                                                className="font-mono text-xs font-bold text-gray-800 block hover:text-brand-600 hover:underline underline-offset-2 text-left leading-tight"
                                              >
                                                {item.item_no}
                                              </button>
                                              {item.description || "-"}
                                              {item.is_overridden && (
                                                <span className="ml-1.5 text-[9px] uppercase tracking-label text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5" data-testid={`cart-item-overridden-badge-${prod.product_id}`}>
                                                  Quote Only
                                                </span>
                                              )}
                                            </div>
                                            {canEdit && (
                                            <button
                                              data-testid={`cart-edit-override-${prod.product_id}`}
                                              onClick={() => openOverride(prod)}
                                              className="text-gray-300 hover:text-brand-600 transition-colors flex-shrink-0"
                                              title="Edit for this quote only"
                                            >
                                              <Pencil size={12} strokeWidth={1.75} />
                                            </button>
                                            )}
                                            {canEdit && (
                                            <span
                                              data-testid={`cart-drag-handle-${prod.product_id}`}
                                              className="text-gray-300 hover:text-brand-600 cursor-grab active:cursor-grabbing flex-shrink-0 -my-0.5"
                                              title="Drag to reorder"
                                            >
                                              <GripVertical size={12} strokeWidth={1.75} />
                                            </span>
                                            )}
                                          </div>
                                        </TableCell>
                                        )}
                                        {visibleCartCols.has("material") && (
                                        <TableCell rowSpan={prod.variants.length} className="text-xs text-gray-500 align-top overflow-hidden" data-testid={`cart-item-material-${prod.product_id}`}>{item.specification || "-"}</TableCell>
                                        )}
                                        {visibleCartCols.has("category") && (
                                        <TableCell rowSpan={prod.variants.length} className="text-xs text-gray-500 align-top overflow-hidden truncate" data-testid={`cart-item-category-${prod.product_id}`}>
                                          {item.category || "-"}
                                          {item.subcategory ? <span className="text-gray-400"> / {item.subcategory}</span> : null}
                                        </TableCell>
                                        )}
                                        {visibleCartCols.has("bulky") && (
                                        <TableCell rowSpan={prod.variants.length} className="text-xs align-top overflow-hidden" data-testid={`cart-item-bulky-${prod.product_id}`}>
                                          <span className={`px-2 py-0.5 rounded-md font-medium ${item.is_bulky ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>{item.is_bulky ? "Yes" : "No"}</span>
                                        </TableCell>
                                        )}
                                        {visibleCartCols.has("customs_duty") && (
                                        <TableCell rowSpan={prod.variants.length} className="font-mono text-sm text-right text-gray-500 align-top" data-testid={`cart-item-customs-duty-${prod.product_id}`}>
                                          {item.customs_duty_rate != null ? `${Number(item.customs_duty_rate).toFixed(2)}%` : "-"}
                                        </TableCell>
                                        )}
                                        {visibleCartCols.has("tax_rebate") && (
                                        <TableCell rowSpan={prod.variants.length} className="font-mono text-sm text-right text-gray-500 align-top" data-testid={`cart-item-tax-rebate-${prod.product_id}`}>
                                          {item.tax_rebate_rate != null ? `${Number(item.tax_rebate_rate).toFixed(2)}%` : "-"}
                                        </TableCell>
                                        )}
                                      </>
                                    )}
                                    {visibleCartCols.has("size") && <TableCell className="text-sm text-gray-500 overflow-hidden truncate">{item.size || "-"}</TableCell>}
                                    {visibleCartCols.has("pcs_ctn") && <TableCell className="font-mono text-sm text-right text-gray-500">{item.pcs_per_ctn ?? "-"}</TableCell>}
                                    {visibleCartCols.has("ci_qty") && <TableCell className="font-mono text-sm text-right text-gray-400" data-testid={`cart-item-ci-inventory-qty-${item.item_id}`}>{item.ci_available ?? item.ci_inventory_qty ?? "-"}{item.ci_reserved_qty ? <span className="block text-[10px] text-emerald-700" data-testid={`cart-item-ci-reserved-${item.item_id}`}>{t("ci.reserved")} {item.ci_reserved_qty}</span> : null}</TableCell>}
                                    {visibleCartCols.has("gt_sku") && <TableCell className="font-mono text-xs text-gray-500 overflow-hidden truncate" data-testid={`cart-item-gt-sku-${item.item_id}`}>{item.gt_sku || "-"}</TableCell>}
                                    {visibleCartCols.has("barcode") && <TableCell className="font-mono text-xs text-gray-500 overflow-hidden truncate" data-testid={`cart-item-barcode-${item.item_id}`}>{item.upc || "-"}</TableCell>}
                                    {visibleCartCols.has("carton_dims") && (
                                    <TableCell className="font-mono text-xs text-right text-gray-500" data-testid={`cart-item-carton-dims-${item.item_id}`}>
                                      {item.carton_length && item.carton_width && item.carton_height ? `${item.carton_length}×${item.carton_width}×${item.carton_height}` : "-"}
                                    </TableCell>
                                    )}
                                    {visibleCartCols.has("qty_40ft") && (
                                    <TableCell className="font-mono text-sm text-right text-gray-500" data-testid={`cart-item-qty-40ft-${item.item_id}`}>
                                      {/* iter 139: prefer server-serialized qty_40ft (respects
                                          manual override); fall back to local compute if the
                                          backend still returns null. */}
                                      {item.qty_40ft != null
                                        ? Number(item.qty_40ft).toLocaleString()
                                        : (() => {
                                            const q = computeContainerQty(item.carton_length, item.carton_width, item.carton_height, item.pcs_per_ctn);
                                            return q != null ? q.toLocaleString() : "-";
                                          })()}
                                    </TableCell>
                                    )}
                                    {visibleCartCols.has("gross_weight") && <TableCell className="font-mono text-sm text-right text-gray-500" data-testid={`cart-item-gross-weight-${item.item_id}`}>{item.gross_weight ?? "-"}</TableCell>}
                                    {visibleCartCols.has("purchase_price") && <TableCell className="font-mono text-sm text-right text-gray-500" data-testid={`cart-item-purchase-price-${item.item_id}`}>{item.purchase_price != null ? `¥${Number(item.purchase_price).toFixed(2)}` : "-"}</TableCell>}
                                    {visibleCartCols.has("price") && (
                                    <TableCell className="font-mono text-sm text-right font-bold text-brand-600">
                                      <div>{formatCurrency(item.price, activeCart.currency)}</div>
                                      {/* iter 138: when currency is CAD, show the customs duty rate
                                          right under the export price (关税率). */}
                                      {activeCart.currency === "CAD" && item.customs_duty_rate != null && (
                                        <div className="text-[10px] font-normal text-gray-500 mt-0.5" data-testid={`cart-item-duty-under-price-${item.item_id}`}>
                                          关税 {Number(item.customs_duty_rate).toFixed(2)}%
                                        </div>
                                      )}
                                    </TableCell>
                                    )}
                                    {visibleCartCols.has("final_price") && (
                                    <TableCell className="text-right">
                                      {canEdit ? (
                                      <Input
                                        type="number"
                                        step="0.01"
                                        defaultValue={item.final_price ?? ""}
                                        onBlur={(e) => updateItem(item.item_id, { final_price: parseFloat(e.target.value) })}
                                        data-testid={`cart-final-price-input-${item.item_id}`}
                                        className="no-spinner w-full h-7 rounded border-transparent bg-transparent shadow-none px-1 text-right font-mono font-bold text-xs hover:border-gray-200 focus:!border-brand-500 focus:!bg-white focus-visible:ring-1 focus-visible:ring-brand-500 transition-colors"
                                      />
                                      ) : (
                                        <span className="font-mono font-bold text-xs" data-testid={`cart-final-price-text-${item.item_id}`}>{item.final_price != null ? Number(item.final_price).toFixed(2) : "-"}</span>
                                      )}
                                    </TableCell>
                                    )}
                                    {visibleCartCols.has("actions") && (
                                    <TableCell>
                                      {canEdit && (
                                      <div className="flex items-center gap-1.5">
                                        {/* iter 134: variant-level up/down arrows. Reorder the
                                            price rows (variants) within a single product. */}
                                        <div className="flex flex-col -my-0.5">
                                          <button
                                            data-testid={`cart-move-variant-up-${item.item_id}`}
                                            onClick={() => handleMoveVariant(gIdx, pIdx, vIdx, -1)}
                                            disabled={vIdx === 0}
                                            className="text-gray-300 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors leading-none"
                                            title="Move variant up"
                                          >
                                            <ChevronUp size={12} strokeWidth={2} />
                                          </button>
                                          <button
                                            data-testid={`cart-move-variant-down-${item.item_id}`}
                                            onClick={() => handleMoveVariant(gIdx, pIdx, vIdx, 1)}
                                            disabled={vIdx === prod.variants.length - 1}
                                            className="text-gray-300 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors leading-none"
                                            title="Move variant down"
                                          >
                                            <ChevronDown size={12} strokeWidth={2} />
                                          </button>
                                        </div>
                                        <button data-testid={`cart-delete-item-${item.item_id}`} onClick={() => handleDeleteItem(item.item_id)} className="text-gray-300 hover:text-red-500 transition-colors">
                                          <Trash2 size={14} strokeWidth={1.75} />
                                        </button>
                                      </div>
                                      )}
                                    </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <EmailQuoteDialog open={emailQuoteOpen} onOpenChange={setEmailQuoteOpen} cartId={activeCartId} cartName={activeCart?.name} />

      {/* iter 220 (2026-02): 图片全屏 lightbox。点表格 / 网格视图的产品缩略图弹出。 */}
      <ImageLightbox
        src={lightbox.src}
        alt={lightbox.alt}
        open={lightbox.open}
        onOpenChange={(v) => setLightbox((s) => ({ ...s, open: v }))}
      />

      <Dialog open={cartColsOpen} onOpenChange={setCartColsOpen}>
        <DialogContent className="rounded-xl sm:max-w-sm" data-testid="cart-columns-dialog">
          <DialogHeader>
            <DialogTitle>Configure Columns</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500">
            Toggle visibility and use ↑/↓ to reorder. Layout is saved to this browser.
          </p>
          <ul className="space-y-1 py-2 max-h-[60vh] overflow-y-auto">
            {cartColOrder.map((id, idx) => (
              <li key={id} className="flex items-center gap-2 border border-gray-100 rounded-md px-2 py-1.5" data-testid={`cart-col-row-${id}`}>
                <Checkbox
                  checked={visibleCartCols.has(id)}
                  onCheckedChange={() => toggleCartCol(id)}
                  data-testid={`cart-col-toggle-${id}`}
                />
                <span className="text-sm text-gray-700 truncate flex-1">{CART_COLUMN_LABELS[id] || <span className="italic text-gray-400">(actions)</span>}</span>
                {/* iter 131: manual reorder via arrows (drag would be
                    nicer but arrows are keyboard-accessible and don't
                    need a DnD library on the critical path). */}
                <button
                  data-testid={`cart-col-up-${id}`}
                  onClick={() => moveCartCol(id, -1)}
                  disabled={idx === 0}
                  className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                  title="Move up"
                >↑</button>
                <button
                  data-testid={`cart-col-down-${id}`}
                  onClick={() => moveCartCol(id, 1)}
                  disabled={idx === cartColOrder.length - 1}
                  className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                  title="Move down"
                >↓</button>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCartColsOpen(false)} className="rounded-lg" data-testid="cart-cols-cancel">Cancel</Button>
            <Button onClick={saveCartColLayout} className="rounded-lg bg-brand-600 hover:bg-brand-700" data-testid="cart-cols-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SampleShipDialog open={sampleShipOpen} onOpenChange={setSampleShipOpen} cartId={activeCartId} onDone={fetchItems} />
      <QuoteItemOverrideDialog
        open={!!overrideProduct}
        onOpenChange={(v) => { if (!v) setOverrideProduct(null); }}
        cartId={activeCartId}
        item={overrideProduct}
        cartItems={items}
        onSaved={fetchItems}
        // iter 230 (2026-02): 传入扁平化产品列表 + 导航回调,让 Dialog
        // 内部实现"前进/后退"按钮 —— 用户不用关闭再打开就能翻看下一
        // 个产品。列表严格按 groups → products 顺序保持一致。
        productList={flatProducts}
        onNavigate={(prod) => openOverride(prod)}
      />

      <ExportFieldsDialog
        open={exportFieldsOpen}
        onOpenChange={setExportFieldsOpen}
        onChange={setExportFields}
      />

      <Dialog open={newQuotationOpen} onOpenChange={setNewQuotationOpen}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="new-quotation-dialog">
          <DialogHeader>
            <DialogTitle>New Quotation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConfirmNewQuotation} className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-gray-600">Quotation Name</Label>
              <Input
                autoFocus
                value={newQuotationName}
                onChange={(e) => setNewQuotationName(e.target.value)}
                placeholder="e.g. customer name or quotation no."
                data-testid="new-quotation-name-input"
                className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              />
            </div>
            {currentUser?.role !== "sample_warehouse" && (
              <div>
                <Label className="text-xs font-medium text-gray-600">{t("warehouse.title_short")}</Label>
                <WarehouseSelect value={newQuotationWarehouse} onChange={setNewQuotationWarehouse} testId="new-quotation-warehouse-select" className="mt-1.5" />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-gray-600">Customer</Label>
              <Select value={newQuotationCustomerId} onValueChange={setNewQuotationCustomerId}>
                <SelectTrigger data-testid="new-quotation-customer-select" className="mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewQuotationOpen(false)} className="rounded-lg">Cancel</Button>
              <Button type="submit" data-testid="new-quotation-confirm-button" className="rounded-lg bg-brand-600 hover:bg-brand-700">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editQuotationOpen} onOpenChange={setEditQuotationOpen}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="edit-quotation-dialog">
          <DialogHeader>
            <DialogTitle>Edit Quotation</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConfirmEditQuotation} className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-gray-600">Quotation Name</Label>
              <Input
                autoFocus
                value={editQuotationName}
                onChange={(e) => setEditQuotationName(e.target.value)}
                data-testid="edit-quotation-name-input"
                className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-gray-600">Customer</Label>
              <Select value={editQuotationCustomerId} onValueChange={setEditQuotationCustomerId}>
                <SelectTrigger data-testid="edit-quotation-customer-select" className="mt-1.5 rounded-lg border-gray-300 focus:ring-brand-500">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditQuotationOpen(false)} className="rounded-lg">Cancel</Button>
              <Button type="submit" data-testid="edit-quotation-confirm-button" className="rounded-lg bg-brand-600 hover:bg-brand-700">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter 128: quotation sharing dialog. Owner-only; picks a user
          from the /users/searchable list, sets edit/read permission,
          shows a list of current shares with per-row remove. */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="rounded-xl sm:max-w-lg" data-testid="share-quotation-dialog">
          <DialogHeader>
            <DialogTitle>Share Quotation — {activeCart?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {shareData.owner && (
              <div className="text-xs text-gray-500">
                Owner: <span className="font-medium text-gray-900">{shareData.owner.name || shareData.owner.email}</span>
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs font-medium text-gray-600">User</Label>
                <Select value={shareForm.user_id} onValueChange={(v) => setShareForm((p) => ({ ...p, user_id: v }))}>
                  <SelectTrigger data-testid="share-user-select" className="mt-1.5 rounded-lg border-gray-300">
                    <SelectValue placeholder="Pick a user" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg max-h-64">
                    {allUsers
                      .filter((u) => u.id !== shareData.owner?.user_id)
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs font-medium text-gray-600">Permission</Label>
                <Select value={shareForm.permission} onValueChange={(v) => setShareForm((p) => ({ ...p, permission: v }))}>
                  <SelectTrigger data-testid="share-permission-select" className="mt-1.5 rounded-lg border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="edit">Edit</SelectItem>
                    <SelectItem value="read">Read-only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="share-add-button" onClick={handleAddShare} className="rounded-lg bg-brand-600 hover:bg-brand-700 h-9">Add</Button>
            </div>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {shareData.shares.length === 0 ? (
                <div className="py-6 text-center text-xs text-gray-400">Not shared with anyone yet</div>
              ) : (
                shareData.shares.map((s) => (
                  <div key={s.user_id} className="flex items-center justify-between px-3 py-2" data-testid={`share-row-${s.user_id}`}>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{s.name || s.email}</div>
                      <div className="text-xs text-gray-500">{s.email} · <span className={s.permission === "edit" ? "text-blue-700" : "text-amber-700"}>{s.permission === "edit" ? "Edit" : "Read-only"}</span></div>
                    </div>
                    <button
                      data-testid={`share-remove-${s.user_id}`}
                      onClick={() => handleRemoveShare(s.user_id)}
                      className="text-gray-400 hover:text-red-600 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
